import { useQuery } from '@apollo/client';

import { UserRole } from '__generated__/globalTypes';
import {
  GetUserPermissions,
  GetUserPermissionsVariables,
} from 'query/__generated__/permissionsQueries';
import { GET_USER_PERMISSIONS } from 'query/permissionsQueries';
import { useUsername } from 'utils/authUtils';

export interface UseHasRoleResult {
  /** True once permissions have loaded and include the required role. */
  hasRole: boolean;
  /** True while the username or permissions query is still resolving. */
  loading: boolean;
}

/**
 * Reactive check for whether the current user holds any of the given roles.
 * Roles come from `GET_USER_PERMISSIONS` (`userManagementGetUser.permissions.roles`),
 * the same source the account/permissions screens use, served cache-first so
 * this is cheap to call from multiple gated buttons on one screen.
 *
 * This is a UX affordance only — the api enforces `@requireRoles` and returns
 * `E-4030 ACCESS_DENIED` regardless, so a stale/absent cache can never grant
 * access it shouldn't.
 */
export function useHasAnyRole(roles: readonly UserRole[]): UseHasRoleResult {
  const username = useUsername();

  const { data, loading } = useQuery<GetUserPermissions, GetUserPermissionsVariables>(
    GET_USER_PERMISSIONS,
    {
      variables: { requestData: { username: username || '' } },
      skip: !username,
      fetchPolicy: 'cache-first',
    },
  );

  // `permissions` is an array of per-company entries; aggregate roles across
  // all of them. The api still enforces the role per company via @requireRoles,
  // so this only widens the *affordance*, never actual access.
  const permissions = data?.userManagementGetUser?.permissions ?? [];
  const heldRoles = permissions.flatMap((p) => p?.roles ?? []);
  const hasRole = heldRoles.some((r) => r !== null && roles.includes(r));

  return { hasRole, loading: loading || !username };
}

/** {@link useHasAnyRole} for a single role. */
export function useHasRole(role: UserRole): UseHasRoleResult {
  return useHasAnyRole([role]);
}
